import { useCallback, useMemo, useState } from 'react';
import { Box, Button, Group, Paper, ScrollArea, SegmentedControl, Stack, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChartBarOff, IconRefresh } from '@tabler/icons-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSecurities } from '@/context/SecuritiesContext';
import type { Security, SecurityPerformanceIntradayPrice, SecurityPerformancePrice } from '@/types';
import { YAXIS_TICK, YAXIS_WIDTH } from '@/utils/chartConstants';
import {
  PERFORMANCE_PERIODS,
  PERFORMANCE_CACHE_VERSION,
  TABLE_PERFORMANCE_PERIODS,
  createPerformanceSeries,
  getCalendarYearReturn,
  getCompletedYears,
  getPerformanceReturn,
  getPortfolioInception,
  getPositivePositionIds,
  getRequiredHistoryStart,
  subtractDays,
  type PerformancePeriod,
  type PerformancePoint,
} from '@/utils/securityPerformance';
import { isTauri } from '@/utils/tauri';

const SERIES_COLORS = [
  '#1791E8',
  '#2D8E50',
  '#DFA638',
  '#D45050',
  '#9775FA',
  '#22B8CF',
  '#F06595',
  '#74B816',
  '#FD7E14',
  '#5C7CFA',
] as const;

interface SecurityWithColor extends Security {
  color: string;
}

interface ChartDatum {
  date: string;
  [securityId: string]: string | number | null;
}

function formatPercent(value: number | null): string {
  if (value === null) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(2)}%`;
}

function valueColor(value: number | null): string {
  if (value === null || value === 0) return 'dimmed';
  return value > 0 ? 'income.6' : 'expense.6';
}

function EmptyState({ message }: { message: string }) {
  return (
    <Paper p="xl" withBorder>
      <Stack align="center" gap="sm" py="xl">
        <IconChartBarOff size={48} stroke={1} color="var(--mantine-color-dimmed)" />
        <Text c="dimmed" ta="center">
          {message}
        </Text>
      </Stack>
    </Paper>
  );
}

function buildChartData(series: Map<string, PerformancePoint[]>): ChartDatum[] {
  const rows = new Map<string, ChartDatum>();
  for (const [securityId, points] of series) {
    for (const point of points) {
      const row = rows.get(point.date) ?? { date: point.date };
      row[securityId] = point.value * 100;
      rows.set(point.date, row);
    }
  }
  return Array.from(rows.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function selectTicks(data: ChartDatum[]): string[] {
  if (data.length <= 8) return data.map((point) => point.date);
  const last = data.length - 1;
  return Array.from({ length: 8 }, (_, index) => data[Math.round((last * index) / 7)]!.date).filter(
    (date, index, values) => index === 0 || date !== values[index - 1]
  );
}

function formatDateTick(date: string, period: PerformancePeriod): string {
  if (period === '1D') return date.includes('T') ? date.slice(11, 16) : `${date.slice(8, 10)} ${date.slice(5, 7)}`;
  if (period === '5D') {
    return date.includes('T') ? `${date.slice(5, 10)} ${date.slice(11, 13)}:00` : `${date.slice(8, 10)} ${date.slice(5, 7)}`;
  }
  if (period === '3Y' || period === '5Y') return date.slice(0, 7);
  return `${date.slice(5, 7)}/${date.slice(2, 4)}`;
}

function formatTooltipDate(date: string | number | undefined, period: PerformancePeriod): string {
  if (date === undefined) return '';
  const value = String(date);
  if (!value.includes('T')) return value;
  return period === '1D'
    ? `${value.slice(0, 10)} ${value.slice(11, 16)} UTC`
    : `${value.slice(0, 10)} ${value.slice(11, 16)} UTC`;
}

function PerformanceTooltip({
  active,
  label,
  payload,
  securities,
  period,
}: {
  active?: boolean | undefined;
  label?: string | number | undefined;
  payload?: { dataKey?: string | number; value?: number; color?: string }[] | undefined;
  securities: SecurityWithColor[];
  period: PerformancePeriod;
}) {
  if (!active || !payload?.length) return null;
  const securityMap = new Map(securities.map((security) => [security.id, security]));
  return (
    <Paper px="sm" py="xs" withBorder shadow="md" style={{ pointerEvents: 'none' }}>
      <Text size="sm" fw={500} mb={4}>
        {formatTooltipDate(label, period)}
      </Text>
      {payload.map((item) => {
        const security = securityMap.get(String(item.dataKey));
        if (!security || typeof item.value !== 'number') return null;
        return (
          <Text key={security.id} size="xs" fw={600} style={{ color: item.color }}>
            {security.ticker}: {formatPercent(item.value / 100)}
          </Text>
        );
      })}
    </Paper>
  );
}

function PerformanceChart({
  chartData,
  securities,
  period,
}: {
  chartData: ChartDatum[];
  securities: SecurityWithColor[];
  period: PerformancePeriod;
}) {
  if (chartData.length < 2 || securities.length === 0) {
    return <EmptyState message="No securities have enough history for this period." />;
  }
  const ticks = selectTicks(chartData);
  const yearBands = period === '3Y' || period === '5Y';
  const years = Array.from(new Set(chartData.map((point) => point.date.slice(0, 4))));
  const yearRanges = years.flatMap((year) => {
    const first = chartData.find((point) => point.date.startsWith(year));
    const last = [...chartData].reverse().find((point) => point.date.startsWith(year));
    return first && last ? [{ year, first: first.date, last: last.date }] : [];
  });

  return (
    <Paper p="md" withBorder>
      <Box h={250}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} vertical={false} />
            {yearBands &&
              yearRanges.map((range, index) =>
                index % 2 === 1 ? (
                  <ReferenceArea
                    key={range.year}
                    x1={range.first}
                    x2={range.last}
                    fill="rgba(128,128,128,0.08)"
                    stroke="none"
                  />
                ) : null
              )}
            <XAxis
              dataKey="date"
              ticks={ticks}
              tickFormatter={(date: string) => formatDateTick(date, period)}
              tick={{ fontSize: 12 }}
              tickLine={false}
              minTickGap={12}
            />
            <YAxis width={YAXIS_WIDTH} tickFormatter={(value: number) => `${value}%`} tick={YAXIS_TICK} />
            <Tooltip
              content={({ active, label, payload }) => (
                <PerformanceTooltip
                  active={active}
                  label={label}
                  payload={payload as { dataKey?: string | number; value?: number; color?: string }[] | undefined}
                  securities={securities}
                  period={period}
                />
              )}
            />
            <Legend formatter={(value: string) => securities.find((security) => security.id === value)?.ticker ?? value} />
            {securities.map((security) => (
              <Line
                key={security.id}
                type="monotone"
                dataKey={security.id}
                stroke={security.color}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

interface TableRow {
  label: string;
  values: Map<string, number | null>;
}

function PerformanceTableHeader({ securities }: { securities: SecurityWithColor[] }) {
  return (
    <Table.Thead>
      <Table.Tr>
        <Table.Th style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--mantine-color-body)' }}>
          Period
        </Table.Th>
        {securities.map((security) => (
          <Table.Th key={security.id} ta="right">
            <Group gap={6} justify="flex-end" wrap="nowrap">
              <Box w={8} h={8} style={{ borderRadius: '50%', background: security.color, flexShrink: 0 }} />
              {security.ticker}
            </Group>
          </Table.Th>
        ))}
      </Table.Tr>
    </Table.Thead>
  );
}

function PerformanceTable({ securities, rows }: { securities: SecurityWithColor[]; rows: TableRow[] }) {
  return (
    <Stack gap="xs">
      <Title order={4}>Performance by Period</Title>
      <Paper withBorder>
        <ScrollArea type="auto">
          <Table miw={Math.max(520, 150 + securities.length * 130)}>
            <PerformanceTableHeader securities={securities} />
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.label}>
                  <Table.Td
                    fw={600}
                    style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--mantine-color-body)' }}
                  >
                    {row.label}
                  </Table.Td>
                  {securities.map((security) => {
                    const value = row.values.get(security.id) ?? null;
                    return (
                      <Table.Td key={security.id} ta="right" ff="monospace" c={valueColor(value)}>
                        {formatPercent(value)}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>
    </Stack>
  );
}

export function SecurityPerformance() {
  const {
    securities,
    securityTransactions,
    securityPerformancePrices,
    securityPerformanceMetadata,
    securityPerformanceIntradayPrices,
    fetchAndCachePerformancePrices,
  } = useSecurities();
  const [period, setPeriod] = useState<PerformancePeriod>('1Y');
  const [isFetching, setIsFetching] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const portfolioInception = getPortfolioInception(securityTransactions);

  const currentSecurities = useMemo(() => {
    const positiveIds = getPositivePositionIds(securityTransactions);
    return securities
      .filter((security) => positiveIds.has(security.id))
      .sort((a, b) => a.ticker.localeCompare(b.ticker))
      .map((security, index) => ({ ...security, color: SERIES_COLORS[index % SERIES_COLORS.length]! }));
  }, [securities, securityTransactions]);

  const pricesBySecurity = useMemo(() => {
    const result = new Map<string, SecurityPerformancePrice[]>();
    for (const price of securityPerformancePrices) {
      const prices = result.get(price.securityId) ?? [];
      prices.push(price);
      result.set(price.securityId, prices);
    }
    return result;
  }, [securityPerformancePrices]);
  const intradayPricesBySecurity = useMemo(() => {
    const result = new Map<string, SecurityPerformanceIntradayPrice[]>();
    for (const price of securityPerformanceIntradayPrices) {
      const prices = result.get(price.securityId) ?? [];
      prices.push(price);
      result.set(price.securityId, prices);
    }
    return result;
  }, [securityPerformanceIntradayPrices]);
  const metadataBySecurity = useMemo(
    () => new Map(securityPerformanceMetadata.map((metadata) => [metadata.securityId, metadata])),
    [securityPerformanceMetadata]
  );

  const chartSeries = useMemo(() => {
    const result = new Map<string, PerformancePoint[]>();
    if (!portfolioInception) return result;
    for (const security of currentSecurities) {
      const series = createPerformanceSeries(
        pricesBySecurity.get(security.id) ?? [],
        period,
        today,
        metadataBySecurity.get(security.id)?.inceptionDate ?? null,
        intradayPricesBySecurity.get(security.id) ?? []
      );
      if (series.length >= 2) result.set(security.id, series);
    }
    return result;
  }, [currentSecurities, intradayPricesBySecurity, metadataBySecurity, period, portfolioInception, pricesBySecurity, today]);
  const chartSecurities = currentSecurities.filter((security) => chartSeries.has(security.id));
  const chartData = useMemo(() => buildChartData(chartSeries), [chartSeries]);
  const hasCurrentPrices = currentSecurities.some((security) => (pricesBySecurity.get(security.id)?.length ?? 0) > 0);

  const tableRows = useMemo(() => {
    if (!portfolioInception) return [];
    const rows: TableRow[] = TABLE_PERFORMANCE_PERIODS.map((tablePeriod) => ({
      label: tablePeriod,
      values: new Map(
        currentSecurities.map((security) => [
          security.id,
          getPerformanceReturn(
            pricesBySecurity.get(security.id) ?? [],
            tablePeriod,
            today,
            metadataBySecurity.get(security.id)?.inceptionDate ?? null,
            intradayPricesBySecurity.get(security.id) ?? []
          ),
        ])
      ),
    }));
    rows.push({
      label: 'YTD',
      values: new Map(
        currentSecurities.map((security) => [
          security.id,
          getPerformanceReturn(
            pricesBySecurity.get(security.id) ?? [],
            'YTD',
            today,
            metadataBySecurity.get(security.id)?.inceptionDate ?? null
          ),
        ])
      ),
    });
    for (const year of getCompletedYears(portfolioInception, today)) {
      rows.push({
        label: String(year),
        values: new Map(
          currentSecurities.map((security) => [
            security.id,
            getCalendarYearReturn(
              pricesBySecurity.get(security.id) ?? [],
              year,
              metadataBySecurity.get(security.id)?.inceptionDate ?? null
            ),
          ])
        ),
      });
    }
    return rows;
  }, [currentSecurities, intradayPricesBySecurity, metadataBySecurity, portfolioInception, pricesBySecurity, today]);

  const handleRefresh = useCallback(async () => {
    if (!portfolioInception || currentSecurities.length === 0) return;
    const requiredStart = getRequiredHistoryStart(portfolioInception, today);
    const tomorrow = new Date(`${today}T00:00:00Z`);
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    const endDate = tomorrow.toISOString().slice(0, 10);
    setIsFetching(true);
    const results = await Promise.allSettled(
      currentSecurities.map(async (security) => {
        const cached = (pricesBySecurity.get(security.id) ?? []).sort((a, b) => a.date.localeCompare(b.date));
        const startDate =
          cached.length === 0 ||
          cached[0]!.date > requiredStart ||
          metadataBySecurity.get(security.id)?.cacheVersion !== PERFORMANCE_CACHE_VERSION
            ? requiredStart
            : subtractDays(cached[cached.length - 1]!.date, 10);
        await fetchAndCachePerformancePrices(security.id, security.ticker, startDate, endDate);
        return security.ticker;
      })
    );
    const failed = results.flatMap((result, index) =>
      result.status === 'rejected' ? [currentSecurities[index]!.ticker] : []
    );
    const succeeded = results.length - failed.length;
    if (failed.length > 0) {
      notifications.show({
        title: succeeded > 0 ? 'Prices partially updated' : 'Refresh failed',
        message: `Failed: ${failed.join(', ')}`,
        color: succeeded > 0 ? 'yellow' : 'red',
      });
    } else {
      notifications.show({
        title: 'Prices updated',
        message: `Fetched performance history for ${succeeded} ${succeeded === 1 ? 'security' : 'securities'}`,
        color: 'green',
      });
    }
    setIsFetching(false);
  }, [currentSecurities, fetchAndCachePerformancePrices, metadataBySecurity, portfolioInception, pricesBySecurity, today]);

  if (securityTransactions.length === 0) {
    return (
      <Stack gap="md">
        <Title order={3}>Securities Performance</Title>
        <EmptyState message="No trades yet. Import trades to compare securities performance." />
      </Stack>
    );
  }
  if (currentSecurities.length === 0) {
    return (
      <Stack gap="md">
        <Title order={3}>Securities Performance</Title>
        <EmptyState message="There are no current positive security positions to compare." />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Securities Performance</Title>
        {isTauri() && (
          <Button leftSection={<IconRefresh size={16} />} onClick={handleRefresh} loading={isFetching}>
            Refresh Prices
          </Button>
        )}
      </Group>
      {!hasCurrentPrices ? (
        <EmptyState message="No performance prices are cached yet. Refresh prices to load market history." />
      ) : (
        <>
          <ScrollArea type="auto">
            <SegmentedControl
              value={period}
              onChange={(value) => setPeriod(value as PerformancePeriod)}
              data={[...PERFORMANCE_PERIODS]}
              fullWidth
              miw={680}
            />
          </ScrollArea>
          <PerformanceChart chartData={chartData} securities={chartSecurities} period={period} />
          <PerformanceTable securities={currentSecurities} rows={tableRows} />
        </>
      )}
    </Stack>
  );
}
